import { formatParisDateTime, type OrderDetail } from '@flux/shared';
import type { JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { Badge } from '@/components/ui/badge.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';

type OrderLine = OrderDetail['lines'][number];
type OrderLineBefore = OrderDetail['linesBefore'][number];
type OrderOperationApplied = OrderDetail['operationsApplied'][number];

function originDescription(
  line: OrderLine,
  operationsApplied: readonly OrderOperationApplied[],
): string {
  const origin = line.origin;
  switch (origin.type) {
    case 'line':
      return origin.sourceLineId
        ? `Ligne d'origine (id ${origin.sourceLineId})`
        : 'Ligne d’origine';
    case 'bundle':
      return origin.bundleSku ? `Bundle « ${origin.bundleSku} »` : 'Bundle';
    case 'operation': {
      const operation = operationsApplied.find((entry) => entry.operationId === origin.operationId);
      return operation ? `Opération commerciale « ${operation.name} »` : 'Opération commerciale';
    }
    case 'system_rule':
      return 'Règle système (TikTok)';
  }
}

/** Onglet 2 « Lignes » (`02` §4) : « avant » (Shopify) / « après » (CSV) avec l'origine de chaque ligne CSV. */
export function LinesTab({ order }: { order: OrderDetail }): JSX.Element {
  const displayLines = order.lines.length > 0 ? order.lines : (order.preview?.lines ?? []);
  return (
    <div className="flex flex-col gap-6">
      <PageSection title="Lignes reçues de Shopify" flush>
        {order.linesBefore.length === 0 ? (
          <p className="px-5 py-4 text-sm text-muted-foreground">Aucune ligne.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SKU</TableHead>
                <TableHead>Titre</TableHead>
                <TableHead className="text-right">Quantité</TableHead>
                <TableHead className="text-right">Quantité actuelle</TableHead>
                <TableHead className="text-right">Prix unitaire</TableHead>
                <TableHead className="text-right">Remise</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {order.linesBefore.map((line: OrderLineBefore) => (
                <TableRow key={line.id}>
                  <TableCell className="whitespace-nowrap font-medium">{line.sku ?? '—'}</TableCell>
                  <TableCell>{line.title ?? '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{line.quantity}</TableCell>
                  <TableCell className="text-right tabular-nums">{line.currentQuantity}</TableCell>
                  <TableCell className="text-right tabular-nums">{line.unitPrice ?? '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{line.discount ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </PageSection>

      <PageSection title={order.lines.length > 0 ? 'Lignes destinées à Martelet' : 'Aperçu des lignes destinées à Martelet'} flush>
        {order.preview && order.lines.length === 0 ? <p className="px-5 pt-4 text-xs text-muted-foreground">Calculé le {formatParisDateTime(new Date(order.preview.calculatedAt))}. Recalculé avant l’envoi réel.</p> : null}
        {displayLines.length === 0 ? (
          <p className="px-5 py-4 text-sm text-muted-foreground">Aucune ligne exportable.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Quantité</TableHead>
                <TableHead className="text-right">Prix unitaire</TableHead>
                <TableHead className="text-right">Remise</TableHead>
                <TableHead>Origine</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayLines.map((line: OrderLine, index) => (
                <TableRow key={`${line.sku}-${String(index)}`}>
                  <TableCell className="whitespace-nowrap font-medium">{line.sku}</TableCell>
                  <TableCell className="text-right tabular-nums">{line.qty}</TableCell>
                  <TableCell className="text-right tabular-nums">{line.unitPrice}</TableCell>
                  <TableCell className="text-right tabular-nums">{line.discount}</TableCell>
                  <TableCell>
                    <Badge tone={line.origin.type === 'line' ? 'gray' : 'blue'}>
                      {originDescription(line, order.operationsApplied)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </PageSection>
    </div>
  );
}
